import { DiagramInfo } from "@/types/diagram";
import {
  Copy,
  Edit,
  MoreVertical,
  Trash,
  Loader2,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import SaveNewDiagram from "./save-new-diagram";
import { generateDiagramSVGContent } from "@/lib/svg-utils.lib";
import useGetDiagram from "@/service-hooks/use-get-diagrams-service";
import useSaveDiagram from "@/service-hooks/use-save-diagram-service";
import useDeleteDiagramService from "@/service-hooks/use-delete-diagram-service";
import useUpdateDiagram from "@/service-hooks/use-update-diagram-service";
import SVGPreview from "@/components/ui/svg-preview";
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { AlertDialog } from "@radix-ui/react-alert-dialog";
import {
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useDiagram } from "@/context/diagram-context";
import { cn } from "@/lib/utils";
import { CUSTOM_SCROLLBAR, PAGE_SIZE } from "@/lib/constants";
import { useAuth } from "react-oidc-context";
import { useParams } from "react-router-dom";
import { DiagramLoader } from "../elements/loader-skeleton";
import { useLibrary } from "@/context/library-context";
import { shapesLibraryManager } from "@/lib/shapes-manager";
import { useUI } from "@/context/ui-context";
import { compileDiagram } from "@/lib/diagram-components.lib";

// Safe hook for UI context
const useSafeUI = () => {
  try {
    return useUI();
  } catch {
    return {
      showAttachmentPoints: false,
    };
  }
};
import { useQueryClient } from "@tanstack/react-query";
import { User } from "@/types";

type Mode = "save" | "clone" | "edit_details" | "delete";
export default function DiagramTab() {
  const diagram = useDiagram();
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { id: uuid } = useParams();
  const { handleMissingDependencies } = useLibrary();
  const { showAttachmentPoints } = useSafeUI();
  const {
    currentDiagramInfo,
    setCurrentDiagramInfo,
    autoSaveState,
    autoSaveStatus,
    isDiagramLoading,
    setIsDiagramLoading,
  } = diagram;
  const [error, setError] = useState({ isError: false, message: "" });
  const [saveError, setSaveError] = useState<string | null>(null);
  const [loadDiagram, setLoadDiagram] = useState(false);
  const [autoSaveMode, setAutoSaveMode] = autoSaveState;
  const user = queryClient.getQueryData<User>(["user"]);
  // Use the diagram service hook
  const {
    data: diagrams,
    refetch,
    isLoading,
  } = useGetDiagram(uuid || "", "architecture", Boolean(uuid));
  // Use delete service hook
  const { mutate: deleteMutation, isPending: isDeletionPending } =
    useDeleteDiagramService(
      () => {
        refetch();
        setIsOpen(false);
      },
      (error) => {
        setError({ isError: true, message: error.message });
      }
    );

  // Use save service hook
  const { mutate, isPending } = useSaveDiagram((res, error) => {
    if (res?._id) {
      refetch();
      handleLoadDiagram(res);
      setIsOpen(false);
      setSaveError(null);
      setError({ isError: false, message: "" });
    }
    if (error) {
      // Handle duplicate name error (409 status)
      const errorData = error as any;
      if (errorData?.status === 409) {
        setSaveError("A diagram with this name already exists. Please choose a different name.");
      } else {
        setSaveError(errorData?.message || "Failed to save diagram. Please try again.");
      }
    }
  });
  // Use update service hook
  const { mutate: updateElement, isPending: isUpdatePending } =
    useUpdateDiagram((res, error) => {
      if (res?._id) {
        refetch();
        setIsOpen(false);
        if (currentDiagramInfo?._id === res._id) handleLoadDiagram(res);
        setSaveError(null);
        setError({ isError: false, message: "" });
      }
      if (error) {
        // Handle duplicate name error (409 status)
        const errorData = error as any;
        if (errorData?.status === 409) {
          setSaveError("A diagram with this name already exists. Please choose a different name.");
        } else {
          setSaveError(errorData?.message || "Failed to update diagram. Please try again.");
        }
      }
    });
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("save");
  const [tempDiagramInfo, setTempDiagramInfo] = useState<DiagramInfo | null>(
    null
  );

  // Listen for save shortcut event
  useEffect(() => {
    const handleSaveShortcut = () => {
      setMode("save");
      setIsOpen(true);
    };

    window.addEventListener("openSaveDiagramModal", handleSaveShortcut);

    return () => {
      window.removeEventListener("openSaveDiagramModal", handleSaveShortcut);
    };
  }, []);
  const getPendingState: Record<Mode, boolean> = {
    save: isPending,
    delete: isDeletionPending,
    edit_details: isUpdatePending,
    clone: isPending,
  };
  const handleDiagramSave = (name: string, description: string) => {
    const svgContent: string = generateDiagramSVGContent(
      diagram.composedSVG,
      diagram.canvasSize
    );

    mutate({
      name,
      description,
      diagramComponents: diagram.diagramComponents,
      svgContent,
      uuid: uuid || "",
    });
  };
  const handleDiagramCloneAndEdit = (name: string, description: string) => {
    const payload = {
      name,
      description,
      diagramComponents: tempDiagramInfo?.diagramComponents ?? [],
      svgContent: tempDiagramInfo?.metadata?.svgContent ?? "",
      uuid: uuid || "",
    };
    if (mode === "clone") mutate(payload);
    if (mode === "edit_details")
      updateElement({ ...payload, id: tempDiagramInfo?._id ?? "" });
  };

  const handleSubmitAccordingToMode = (name: string, description: string) => {
    if (mode === "save") handleDiagramSave(name, description);
    if (mode === "clone" || mode === "edit_details")
      handleDiagramCloneAndEdit(name, description);
    if (mode === "delete") handleDeleteDiagram();
  };

  const handleDialogOperations = (element: DiagramInfo, mode: Mode) => {
    setTempDiagramInfo({
      ...element,
      name: mode === "clone" ? element.name + " clone" : element.name,
    });
    setMode(mode);
    setSaveError(null); // Clear any previous errors
    setIsOpen(true);
  };

  const handleCloseDialog = () => {
    setIsOpen(false);
    setSaveError(null); // Clear errors when closing
  };

  const handleClearSaveError = () => {
    setSaveError(null);
  };

  const handleDeleteDiagram = async () => {
    if (!tempDiagramInfo?._id) return;
    if (currentDiagramInfo?._id === tempDiagramInfo._id) {
      diagram.setDiagramComponents([]);
      setCurrentDiagramInfo(null);
    }
    deleteMutation(tempDiagramInfo?._id);
  };

  const handleLoadDiagram = async (element: DiagramInfo) => {
    if (isDiagramLoading) return;
    // if (element?.uuid) {
    //   const currentUrl = new URL(window.location.href);
    //   currentUrl.searchParams.delete("uuid");
    //   currentUrl.searchParams.delete("diagram_id");
    //   currentUrl.searchParams.append("uuid", element.uuid);
    //   if (element._id)
    //     currentUrl.searchParams.append("diagram_id", element._id);
    //   window.history.pushState({}, "", currentUrl);
    // }

    if (currentDiagramInfo?._id !== element._id) setIsDiagramLoading(true);
    await handleMissingDependencies(element.diagramComponents || []);
    const { svgContent, processedComponents } = compileDiagram(
      element.diagramComponents || [],
      diagram.canvasSize,
      shapesLibraryManager.getAllShapes(),
      showAttachmentPoints
    );
    diagram.setDiagramComponents(processedComponents);
    diagram.setComposedSVG(svgContent);
    diagram.selectShape(null);
    setIsDiagramLoading(false);
    if (element._id === currentDiagramInfo?._id) return;
    setCurrentDiagramInfo(element);
    setAutoSaveMode(auth.user?.profile?.sub === element.userId);
  };

  const handleLoadDiagramConfirmation = (element?: DiagramInfo) => {
    setTempDiagramInfo(element || null);
    setLoadDiagram(Boolean(element));
  };

  const DiagramDetails = ({ result }: { result: DiagramInfo }) => {
    const isMyDiagram = user?._id === result.userId;
    const isSelected = result._id === currentDiagramInfo?._id;
    return (
      <li
        className={cn(
          "flex rounded-md items-center my-3 p-3 cursor-pointer transition-colors",
          "hover:bg-surface-hover border border-surface-border focus:outline-2 focus:outline-primary focus:outline-offset-2",
          isSelected
            ? "bg-surface-elevated border-primary"
            : "bg-surface-active"
        )}
        onClick={() => handleLoadDiagramConfirmation(result)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleLoadDiagramConfirmation(result);
          }
        }}
        tabIndex={0}
        role="button"
        aria-label={`${result.name}${isSelected ? ", currently loaded" : ""}. ${result.metadata?.description || "No description"}. Version ${result.version}. Press Enter to load.`}
        aria-pressed={isSelected}
      >
        <div className={`flex flex-grow items-center gap-4`}>
          <div className="w-[76px] h-[76px] flex-shrink-0">
            <SVGPreview
              svgContent={result.metadata?.svgContent ?? ""}
              className="w-full h-full object-cover bg-white"
            />
          </div>
          <div className="flex-1">
            <div className="text-white font-semibold">{result.name}</div>
            <p className="text-muted text-xs">{result.metadata?.description}</p>
            <span className="mt-2 inline-block text-xs font-medium text-primary">
              {result.version}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1 pr-4">
          <div className="flex items-center gap-2">
            {isMyDiagram &&
              autoSaveMode &&
              currentDiagramInfo?._id === result._id && (
                <>
                  <Edit size={16} />
                  {autoSaveStatus === "saving" && (
                    <div className="flex items-center text-blue-400">
                      <Loader2 size={14} className="animate-spin" />
                      <span className="text-xs ml-1">Saving...</span>
                    </div>
                  )}
                  {autoSaveStatus === "saved" && (
                    <div className="flex items-center text-green-400">
                      <CheckCircle size={14} />
                      <span className="text-xs ml-1">Saved</span>
                    </div>
                  )}
                  {autoSaveStatus === "error" && (
                    <div className="flex items-center text-red-400">
                      <AlertCircle size={14} />
                      <span className="text-xs ml-1">Error</span>
                    </div>
                  )}
                </>
              )}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Options for ${result.name}`}
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="w-5 h-5" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-32 bg-surface border-surface-border"
            >
              {isMyDiagram && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();

                    handleDialogOperations(result, "edit_details");
                  }}
                  className="flex items-center gap-2 cursor-pointer text-white hover:bg-surface-hover"
                >
                  <Edit size={16} />
                  Edit Details
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleDialogOperations(result, "clone");
                }}
                className="flex items-center gap-2 cursor-pointer text-white hover:bg-surface-hover"
              >
                <Copy size={16} />
                Clone
              </DropdownMenuItem>
              {isMyDiagram && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDialogOperations(result, "delete");
                  }}
                  className="flex items-center gap-2 text-error cursor-pointer hover:bg-surface-hover"
                >
                  <Trash size={16} />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </li>
    );
  };

  const closeAlert = () => setError({ isError: false, message: "" });
  return (
    <div
      className="flex flex-col flex-1 min-h-0 bg-background text-white"
      role="region"
      aria-label="Saved diagrams panel"
    >
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
        <div className={cn("flex-1 min-h-0 overflow-auto p-4", CUSTOM_SCROLLBAR)}>
          {isLoading ? (
            <div role="status" aria-label="Loading diagrams">
              {Array.from({ length: PAGE_SIZE }).map((_, index) => (
                <DiagramLoader key={index} />
              ))}
              <span className="sr-only">Loading diagrams...</span>
            </div>
          ) : diagrams?.data?.length === 0 ? (
            <div className="flex items-center justify-center h-full text-muted" role="status">
              <div className="text-center">
                <p className="text-lg mb-2">No diagrams yet</p>
                <p className="text-sm">
                  Create and save your first diagram to see it here
                </p>
              </div>
            </div>
          ) : (
            <ul role="list" aria-label={`Saved diagrams, ${diagrams?.data?.length} items`}>
              {diagrams?.data.map((item: DiagramInfo) => (
                <DiagramDetails key={item._id} result={item} />
              ))}
            </ul>
          )}
        </div>
      </div>
      <AlertDialog open={error.isError} onOpenChange={closeAlert}>
        <AlertDialogContent className="bg-surface border-surface-border text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Error</AlertDialogTitle>
            <AlertDialogDescription>{error.message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={closeAlert}>Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={loadDiagram} onOpenChange={setLoadDiagram}>
        <AlertDialogContent className="bg-surface border-surface-border text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Load Diagram: {tempDiagramInfo?.name}
            </AlertDialogTitle>
            <AlertDialogDescription>
              You are about to load the <strong>{tempDiagramInfo?.name}</strong>{" "}
              diagram.
              <br />
              <span className="text-red-400">
                Warning: Loading this diagram will overwrite your current
                progress.
              </span>
              <br />
              If you want to keep your changes, click <strong>Cancel</strong>
              and save your current diagram first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => handleLoadDiagramConfirmation()}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                tempDiagramInfo && handleLoadDiagram(tempDiagramInfo)
              }
              className="text-button-text bg-primary border-primary hover:bg-primary-dark hover:text-button-text"
            >
              Confirm & Load
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SaveNewDiagram
        mode={mode}
        isOpen={isOpen}
        isPending={getPendingState[mode]}
        onClose={handleCloseDialog}
        onSubmit={handleSubmitAccordingToMode}
        diagramInfo={tempDiagramInfo}
        error={saveError}
        onErrorClear={handleClearSaveError}
      />
    </div>
  );
}
